import { Snackbar, Tooltip } from "@material-ui/core";
import { Events, Collection } from "@type-r/models";
import { pureRenderProps, useEvent, useLinked, useModel } from "@type-r/react";
import { asCurrency, Button, CollapsableSection, div, PrimaryButton, ReadOnly, Subsection } from 'lqx-react';
import * as React from 'react';
import { Fragment } from "react";
import { globalStore } from "api/store";
import { useModelCopySync } from "common";
import { Payment, RecsInvoice, RemittanceAdvice } from 'src/app/pages/inmatch/api';
import { PaymentInfoIcon, RemittanceAdviceIcon, RowH4 } from "../../../../../../common/inmatch";
import { Delta } from "./delta";
import { SmallInvoiceTile } from "./invoicesList";
import { PaymentHeader } from "./payment-header";
import { MatchedInvoices, UnfundedInvoices } from "./potential-matches";
import { ReconcileDialog } from "./reconcile-payment";
import { SelectRemittanceAdvice, ShowRemittanceAdvice } from "./remittanceAdviceTab";
import { UpdateConversationDialog } from "./update-conversation";
import { ReprocessPaymentDialog } from "./reprocess-payment";
import { OpenInvoices } from "./open-invoices";
import { Alert } from "@material-ui/lab";
import { InmatchRecommendations } from "./recommendations";
import { isFeatureEnabled } from "api/common";
import { PlatformCompanyConfig } from "../../api/common";
import { Can } from "src/app/services/can";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";

export const PaymentDetails = ( { payment, getPayment, allPayments, platformCompanyConfigs } : { payment: Payment, getPayment: Function, allPayments: Collection<Payment>, platformCompanyConfigs: Collection<PlatformCompanyConfig> }) => {
    return (
        <Fragment key={payment.cid}>
            <ReadOnly.Provider value={ false }>
                <PaymentActions payment={ payment } allPayments={allPayments} platformCompanyConfigs={platformCompanyConfigs}/>
                <OpenInvoices getPayment= { getPayment } payment={payment} />
            </ReadOnly.Provider>
        </Fragment>
    )
};



const PaymentActions = ({ payment, allPayments, platformCompanyConfigs } : { payment: Payment, allPayments: Collection<Payment>, platformCompanyConfigs: Collection<PlatformCompanyConfig> }) => {
    const localPayment: Payment = useModelCopySync(payment);
    const $approval = useLinked(false);
    const $sendTo = useLinked(false);
    const $updateConversation = useLinked(false);
    const $reprocessPayment = useLinked(false);

    const readOnly = React.useContext(ReadOnly);

    // TODO: fix clone to pin the store.
    (localPayment as any)._defaultStore = payment.getStore();

    useEvent( Events, 'invoices:add', async invoice => {
        const [ match ] = localPayment.matches.add({
            appliedAmount : invoice.unreconciledAmount,
            invoiceId : invoice.id,
            isManual : true,
            isActive: true,
            originType: 'manual',
        });
        
        try { 
            await match.save();
            await localPayment.invoices.fetch();

            payment.assignFrom( localPayment );
        } catch (e) {
            $openErrorSnackBar.true();
            localPayment.matches.remove(match);
        }
        
    });

    useEvent( localPayment.matches, 'remove', () => {
        payment.assignFrom( localPayment );
    });

    useEvent( localPayment.invoices, 'remove', () => {
        payment.assignFrom( localPayment );
    });

    useEvent( Events, 'invoices:addMultiple', async matches => {
        await localPayment.invoices.fetch(),
        localPayment.matches.push(matches);

        payment.assignFrom( localPayment );
    });

    useEvent( Events, 'invoices:unmatch',  selectedInvoices =>  {
        allPayments.updateEach(inv => {
            inv.matches.set(inv.matches.filter(item => !selectedInvoices.includes(item.invoiceId)))
        })
    });

    useEvent( Events, 'matches:unmatch', selectedMatches => {
        const matches = localPayment.matches.filter(inv => !selectedMatches.includes(inv.invoiceId));
        localPayment.matches.reset();
        localPayment.matches.push(matches);

        payment.assignFrom( localPayment );
    });

    const remittanceAdviceRequired = !!platformCompanyConfigs.length && (platformCompanyConfigs.last().remittanceAdviceRequired !== undefined || platformCompanyConfigs.first().remittanceAdviceRequired);

    const isValid = localPayment.matches.length && localPayment.isWithinTolerance(localPayment.delta) && localPayment.finalCashApplied > 0.00001
                    && (!localPayment.remittanceAdvice || !localPayment.remittanceAdvice.isMissingSupplierName);

    const canReprocess = true;

    const assignDelta = () => {
        if (localPayment.delta < 0.00001) {
            localPayment.deductables.processingFees += Math.abs(localPayment.delta);
        }
    };

    const $openErrorSnackBar = useLinked(false);

    const unfundedMatches = localPayment.matches.filter(inv => !inv.invoiceId && !!inv.invoiceNumber);

    const showRaSection = payment.remittanceAdviceId || 
        remittanceAdviceRequired ||
        payment.remittanceAdvices.length;

    React.useEffect(() => {
        if (localPayment.matches) {
            const counts = localPayment.matches.groupBy(
                'originType',
                (len : number = 0, inv) => len + (inv.isActive ? 1 : 0)
            );
        
            const { remittance_info, remittance_advice, heuristic, manual, greedy } = counts;

            const unfundedInvoiceCount = localPayment.matches.reduce((sum: number, curr) => sum + ((!curr.invoiceId && !!curr.invoiceNumber) ? 1 : 0), 0);
            const recommendationMatchesCount = localPayment.matches.filter(m => !m.isActive && !!m.recommendationId);

            localPayment.set({...localPayment, 
                matchesAggregation: {
                    count: localPayment.matches.length,
                    appliedAmount: localPayment.cashApplied,
                    unfundedCount: unfundedInvoiceCount,
                    remittanceInfoCount: remittance_info,
                    remittanceAdviceCount: remittance_advice,
                    heuristicCount: heuristic,
                    manualCount: manual,
                    greedyCount: greedy,
                    recommendationsCount: recommendationMatchesCount,
                }
            });

            payment.assignFrom(localPayment);
        }
    }, [localPayment.matches.length]); 
//button in here
    return (
        <>
        <PaymentHeader
            payment={ localPayment }
        >
            
        </PaymentHeader>

        <Delta
            payment={ localPayment }
            right={
                <>
                    <RowH4>{ localPayment.matches.length } Matches
                        { ' Found' }
                    </RowH4>
                    {Can(UserEntitlementCatalogs.BankEntryWrite) ? 
                    <>
                    <PrimaryButton
                        color="yellow"
                        disabled={ !isValid }
                        hidden={ readOnly }
                        onClick={ () => { 
                            assignDelta();
                            payment.assignFrom(localPayment); 
                            $approval.true(); } }
                    >
                        Approve
                    </PrimaryButton>
                    <Button
                        disabled={ !canReprocess }
                        hidden={ readOnly }
                        onClick={ () => { 
                            //assignDelta();
                            payment.assignFrom(localPayment); 
                            $reprocessPayment.true(); } }
                    >
                        Reprocess Payment
                    </Button>
                    <Tooltip title={(payment.matches && !!payment.matches.length) || !!payment.remittanceAdviceId ? 'You cannot update payment having invoice matches or linked with RA' : ''}>
                        <span>
                        <PrimaryButton
                            disabled={(payment.matches && !!payment.matches.length) || !!payment.remittanceAdviceId}
                            color="yellow"
                            hidden={ readOnly }
                            onClick={ () => { payment.assignFrom(localPayment); $updateConversation.true(); } }
                        >
                            Update relationship
                        </PrimaryButton>
                        </span>
                    </Tooltip></> : undefined }
                </>
            }
        />

        <PaymentInfo payment={ payment } />
        { showRaSection ? <RemittanceAdviceInfo payment={ payment } /> : undefined }

        <InmatchRecommendations payment={ payment } /> 

        <MatchedInvoices matches={ localPayment.matches } paymentUnreconciledAmount={ localPayment.unreconciledAmount } paymentId={localPayment.id} currency={localPayment.currency} showExport
            invoices={localPayment.invoices} showCustomer
        />
        
        { 
            unfundedMatches && unfundedMatches.length ? 
                <UnfundedInvoices matches={ unfundedMatches } currency={localPayment.currency} remitanceAdviceId={localPayment.remittanceAdviceId} />
            : undefined }

        { $approval.value ?
            <ReconcileDialog payment={ localPayment } $isOpen={ $approval } stage="Approve" />
        : null }

        { $sendTo.value ?
            <ReconcileDialog payment={ localPayment } $isOpen={ $sendTo } stage="Send" />
        : null }

        { $reprocessPayment.value ?
            <ReprocessPaymentDialog payment={ localPayment } $isOpen={ $reprocessPayment } />
        : null }
        
        { $updateConversation.value ?
            <UpdateConversationDialog payment={ localPayment } $isOpen={ $updateConversation } />
        : null }

        <Snackbar
            anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
            open={$openErrorSnackBar.value}
            autoHideDuration={10000}
            onClose={() => $openErrorSnackBar.false()}
        >
            <Alert severity="error">
                Not able to match. Please  refresh and try again.
            </Alert>
        </Snackbar>
        </>
    );
};


const PaymentInfo = pureRenderProps({
        payment : Payment
    },
    ({ payment }) => {
        const { paymentDetail, paymentInfoInvoices, currency } = payment,
            invoicesFound = paymentInfoInvoices ? paymentInfoInvoices.length : 0;

            return (
            <CollapsableSection notExpanded
                title="Payment Remittance"
                info={
                    <>
                        <PaymentInfoIcon invalid={ !paymentInfoInvoices }/>
                        { paymentInfoInvoices ?
                            `${invoicesFound} invoice${invoicesFound == 1 ? "" : "s"} - ` +
                            asCurrency( paymentInfoInvoices.reduce( ( a : number, b : RecsInvoice ) => a + b.unreconciledAmount, 0 ), currency ) :
                            'No Invoices'
                        }
                    </>
                }
            >
                <FlexWrapRow>
                    { paymentInfoInvoices ?
                        paymentInfoInvoices.map( ( x : RecsInvoice ) => (
                            <SmallInvoiceTile key={ x.cid }
                                invoice={ x }
                                paymentId={ payment.id }
                            />
                        ))
                    :
                        null
                    }
                </FlexWrapRow>

                <Subsection title="Remittance Info">
                    { paymentDetail.remittanceInfoUnstructured || <i>No info</i> }
                </Subsection>

                <Subsection title="Additional Entry Info">
                    { paymentDetail.additionalEntryInfo || <i>No info</i> }
                </Subsection>
            </CollapsableSection>
        )   
    }
)

const FlexWrapRow = div({
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap'
})

export const RemittanceAdviceInfo = ({ payment, disabled, expanded, showExport, right } : {
    payment : Payment, 
    disabled? : boolean,
    expanded? : boolean,
    showExport? : boolean,
    right?: React.ReactNode
}) => {
    const { remittanceAdviceId } = payment;

    return (
        <CollapsableSection notExpanded={!expanded}
            title="Remittance Advice"

            info={
                <>
                <RemittanceAdviceIcon invalid={ !remittanceAdviceId } />
                { remittanceAdviceId ? '#' + remittanceAdviceId : 'None' }
                </>
            }
            right={
                <>
                    {right}
                </>
            }
        >
            { remittanceAdviceId ?
                <ShowRemittanceAdvice payment={ payment } disabled={disabled} showExport={ showExport } /> :
                <SelectRemittanceAdvice payment={ payment } /> 
            }
        </CollapsableSection>
    )
}
